import * as React from 'react';
import styled from 'styled-components';
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";

import { ListView } from '@longline/aqua-ui/controls/ListView';
import { SVG } from '@longline/aqua-ui/svg';
import { Input } from '@longline/aqua-ui/inputs/Input';
import { Message } from '@longline/aqua-ui/containers/Message';
import { Form } from '@longline/aqua-ui/containers/Form';

import { DriverParameters, IDriverDate, IDriverParameter, IDriverset, TDriversetParameter } from '../../api/models';
import { TinyButton } from './TinyButton';
import { DriversetImportReport } from './DriversetImportReport';
import { DriversetExcel, IDriversetImportResult } from '../../util/DriversetExcel';
import { PANE_WIDE } from '../../types/Constants';

interface IProps {
  driverset: IDriverset;
  onChange: (driverset: IDriverset, valid: boolean) => void;
  withName?: boolean;
}

const DriversetEditor = (props: IProps) => {
  // Ensure we are working with a deep copy of the driverset.
  const copy: IDriverset = JSON.parse(JSON.stringify(props.driverset));
  // Give each date a unique index.
  const { current: ds } = React.useRef({
    ...copy,
    dates: copy.dates.map((d, i) => ({ ...d, index: i })),
  });
  const [driverset, setDriverset] = React.useState({...ds});
  const [report, setReport] = React.useState<{ filename: string, result: IDriversetImportResult } | null>(null);
  const fileRef = React.useRef<HTMLInputElement | null>(null);

  /**
   * All Julian days must be filled out, and there cannot be any duplicate
   * days.
   */
  const areDaysValid = (d: IDriverset): boolean => {
    const seenDays = new Set<number>();
    // 4. Julian days must all be filled out and valid integers 1..365:
    for (const date of d.dates) {
      if(date.day == null || isNaN(date.day)) return false;

      const day = Number(date.day);
      if (!Number.isInteger(day) || day < 1 || day > 365) return false;

      // Check for duplicates
      if (seenDays.has(day)) return false;
      seenDays.add(day);      
    }
    return true;
  }

  /**
   * To be valid, a parameter must have a least one value across all dates.
   * Validate parameter at index and return true if valid.
   */
  const isParameterValid = (d: IDriverset, name: TDriversetParameter): boolean => {
    const values = d.dates.map(dates => dates.values[name]);
    const hasValue = values.find(v => v != null);
    return hasValue !== undefined;
  }

  /** 
   * To be valid, a date must have a value for at least one of its
   * parameters. Validate date at index and return true if valid.
   */
  const isDateValid = (d: IDriverset, idx: number): boolean => {
    const date = d.dates[idx];
    if (!date) return false;

    const valuesObj = date.values;
    if (!valuesObj) return false;

    let hasValue = false;

    // iterate through each parameter name
    for (const paramName of Object.keys(valuesObj) as TDriversetParameter[]) {
      const value = valuesObj[paramName];
      if(value === null) continue;
      if(isNaN(value)) return false;
      hasValue = true;
    }

    return hasValue;
  }

  //
  // Is the given driver set valid?
  // Rules:
  // - Must have valid name
  // - Each parameter must have a value for at least one day
  // - Must have at least 1 date
  // - Date must be valid integer between 1 and 365
  // - Each date must have at least 1 non-null value
  // - Each value must be null or a valid float
  //
  const isDriversetValid = (driverset: IDriverset): boolean => {
    // Validate name:
    if (!props.withName && typeof driverset.name !== 'string' || driverset.name.length < 1 || driverset.name.length > 255) {
      return false;
    }

    // Must have at least one date:
    if (!Array.isArray(driverset.dates) || driverset.dates.length === 0) {
      return false;
    }

    // Julian days must all be filled out and valid integers 1..365:
    if(!areDaysValid(driverset)) return false;

    // Validate values in each date. Each value must be either null
    // or a float. Each date must have at least one non-null value.
    for (let i = 0; i < driverset.dates.length; i++) {
      if(!isDateValid(driverset, i)) return false;
    }

    // Check that each parameter has at least one value.
    for(let name of DriverParameters.map(dp => dp.name)) {
      if(!isParameterValid(driverset, name)) return false;
    }

    // All checks passed
    return true;
  }

  const handleChange = () => {
    props.onChange(ds, isDriversetValid(ds));
  }

  const handleUpdateName = (v: string) => {
    ds.name = v;
    handleChange();
  }

  const handleUpdateDay = (date: IDriverDate, newDayValue: string) => {
    const dateIdx = ds.dates.findIndex(d => d.index == date.index);
    if (dateIdx === -1) return;
    ds.dates[dateIdx].day = newDayValue === "" ? null : Number(newDayValue);
    handleChange();
  }

  const handleUpdateValue = (date: IDriverDate, param: IDriverParameter, newValue: string) => {
    const dateIdx = ds.dates.findIndex(d => d.index == date.index);
    if (dateIdx === -1) return;
    ds.dates[dateIdx].values[param.name] = newValue === "" ? null : Number(newValue);
    handleChange();
  }

  const handleExport = () => DriversetExcel.export(ds);

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Cleared so that choosing the same file twice still fires a change.
    e.target.value = '';
    if(!file) return;

    const result = await DriversetExcel.import(file);
    if(!result.fatal) {
      // Import replaces every row. Fresh indices because the editor identifies
      // rows by index and the old ones belong to rows that no longer exist.
      ds.dates = result.dates.map((d, i) => ({ ...d, index: i }));
      setDriverset({ ...ds, dates: ds.dates });
      handleChange();
    }
    // Shown even on a clean import, so a silently ignored column is visible.
    setReport({ filename: file.name, result });
  }

  const handleAddDay = () => {
    ds.dates = [...ds.dates, { day: null, values: {} as IDriverDate['values'], index: Date.now() } ];
    setDriverset({ ...ds, dates: ds.dates });
    handleChange();
  }

  const handleDeleteDay = (date: IDriverDate) => {
    ds.dates = ds.dates.filter(d => d.index != date.index);
    setDriverset({ ...ds,  dates: ds.dates });
    handleChange();
  }

  return (
    <>
      {props.withName && <div style={{padding: '16px 16px 8px 16px'}}>
        <Form.Field 
          rules={{
            required: { message: "Name is required." },
            maxLength: { length: 255, message: "No more than 255 characters." }
          }}
          name="name"
          label="Name"
          hint="Driver set name"
          value={driverset.name}
          onChange={(v) => handleUpdateName(v)}
          wrapper="box"
          control={<Input name="name"/>}
        />
      </div>}
      <Toolbar>
        <TinyButton
          title="Export this driver set to an Excel file"
          icon={SVG.Icons.Export}
          onClick={handleExport}
          label="Export to Excel"
        />
        <TinyButton
          title="Replace every row from an Excel file"
          icon={SVG.Icons.Download}
          onClick={() => fileRef.current?.click()}
          label="Import from Excel"
        />
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
          onChange={handleImportFile}
        />
      </Toolbar>
      <OverlayScrollbarsComponent 
        element="div"
        options={{ scrollbars: { theme: 'os-theme-light', autoHide: 'leave' }}}
        style={{
          position: 'relative',
          flex: 1,
          overflowX: 'scroll',
        }}
      >
        <ListScroller>
          <ListView
            dark
            shadow
            rowHeight={56}
            data={driverset.dates.map((d,idx) => ({...d, error: !isDateValid(driverset, idx)}))}
            grid
            noData={<Message.Error margin>No drivers.</Message.Error>}
          >
            {/* Day column */}
            <ListView.Column active error={!areDaysValid(driverset)} name="day" label={<TinyButton positive title="Add" icon={SVG.Icons.Plus} onClick={handleAddDay} label="Julian day"/>}>
              {item => 
                <ControlWrapper>
                  <Form.Field
                    rules={{
                      required: {},
                      isInt: {},
                      min: { value:  1 },
                      max: { value: 365 }
                    }}
                    name=""
                    value={item.day} onChange={(v) => handleUpdateDay(item, v)}
                    control={<Input fluid/>}
                  />
                </ControlWrapper>}
            </ListView.Column>
            {/* Parameter columns */}
            {DriverParameters.map((p, idx) => 
              <ListView.Column key={idx} error={!isParameterValid(driverset, p.name)} active width={1} name={`p${idx}`} 
                label={p.label}  
              >
                {(item: IDriverDate) => 
                  <ControlWrapper>
                    <Form.Field
                      rules={{
                        isFloat: {}
                      }}
                      name=""
                      value={item.values[p.name] ?? null} 
                      onChange={(v) => handleUpdateValue(item, p, v)}
                      control={<Input fluid/>}
                    />                
                  </ControlWrapper>}
              </ListView.Column>
            ) as any}
            {/* Delete row column */}
            <ListView.Column active width="80px" name="" label={""}>
              {item => <TinyButton negative title="Delete" icon={SVG.Icons.Minus} onClick={() => handleDeleteDay(item)}/>}
            </ListView.Column>
          </ListView>
        </ListScroller>
      </OverlayScrollbarsComponent>
      {report && <DriversetImportReport
        filename={report.filename}
        result={report.result}
        onClose={() => setReport(null)}
      />}
    </>
  )
}

const Toolbar = styled.div`
  display: flex;
  gap: 24px;
  padding: 12px 16px;
`

const ListScroller = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  //min-width: ${PANE_WIDE};
  display: flex;
  background-color: green;
`

// Controls need wrapping so that their outline isn't cut off.
const ControlWrapper = styled.div`
  padding: 2px;
`

export { DriversetEditor }

